import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

interface HostRouteProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that protects host routes.
 * Allows access for hosts AND admins (admin can always access host pages for debugging).
 * If user lacks host/admin role, tries one forced token refresh (handles post-approval stale token).
 */
export function HostRoute({ children }: HostRouteProps) {
  const { user, loading, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const hasRetried = useRef(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (loading || retrying) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user?.isHost && !user?.isAdmin) {
      if (!hasRetried.current) {
        // Token may be stale after admin approval — force refresh once
        hasRetried.current = true;
        setRetrying(true);
        refreshUser().finally(() => setRetrying(false));
      } else {
        navigate('/');
      }
    }
  }, [loading, retrying, isAuthenticated, user, navigate, refreshUser]);

  if (loading || retrying) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && (user?.isHost || user?.isAdmin)) {
    return <>{children}</>;
  }

  return null;
}
